/** Mirrors `UpdateProfileDto` / `ProfilePromptsDto` limits in services/api. */
export const PROFILE_FIELD_LIMITS = {
  displayName: { min: 2, max: 50 },
  username: { min: 3, max: 30 },
  bio: { max: 500 },
  headline: { max: 120 },
  location: { max: 100 },
  topicTag: { max: 40 },
  maxTopics: 12,
  prompt: { max: 200 },
} as const;

export type ProfileValidationField =
  | 'displayName'
  | 'bio'
  | 'headline'
  | 'location'
  | 'topics'
  | 'profilePrompts';

export type ProfileValidationErrors = Partial<Record<ProfileValidationField, string>>;

export type ProfilePatchInput = {
  displayName?: string;
  bio?: string;
  headline?: string;
  location?: string;
  topics?: string[];
  profilePrompts?: {
    workingOn?: string | null;
    learning?: string | null;
    askMeAbout?: string | null;
  };
};

const PROFILE_VALIDATION_ORDER: ProfileValidationField[] = [
  'displayName',
  'bio',
  'headline',
  'location',
  'topics',
  'profilePrompts',
];

function validateDisplayName(value: string | undefined, required: boolean): string | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return required ? 'Name is required' : undefined;
  }
  if (trimmed.length < PROFILE_FIELD_LIMITS.displayName.min) {
    return `Name must be at least ${PROFILE_FIELD_LIMITS.displayName.min} characters`;
  }
  if (trimmed.length > PROFILE_FIELD_LIMITS.displayName.max) {
    return `Name must be ${PROFILE_FIELD_LIMITS.displayName.max} characters or fewer`;
  }
  return undefined;
}

function validateMaxLength(
  value: string | undefined,
  max: number,
  label: string,
): string | undefined {
  if (value === undefined) return undefined;
  if (value.length > max) {
    return `${label} must be ${max} characters or fewer`;
  }
  return undefined;
}

function validateTopics(topics: string[] | undefined): string | undefined {
  if (!topics) return undefined;
  if (topics.length > PROFILE_FIELD_LIMITS.maxTopics) {
    return `Choose up to ${PROFILE_FIELD_LIMITS.maxTopics} topics`;
  }
  const tooLong = topics.find((tag) => tag.trim().length > PROFILE_FIELD_LIMITS.topicTag.max);
  if (tooLong) {
    return `Each topic must be ${PROFILE_FIELD_LIMITS.topicTag.max} characters or fewer`;
  }
  return undefined;
}

function validatePrompts(
  prompts: ProfilePatchInput['profilePrompts'],
): string | undefined {
  if (!prompts) return undefined;
  const entries: Array<[string, string | null | undefined]> = [
    ['Working on', prompts.workingOn],
    ['Learning', prompts.learning],
    ['Ask me about', prompts.askMeAbout],
  ];
  for (const [label, value] of entries) {
    if (value && value.length > PROFILE_FIELD_LIMITS.prompt.max) {
      return `${label} must be ${PROFILE_FIELD_LIMITS.prompt.max} characters or fewer`;
    }
  }
  return undefined;
}

export type ValidateProfilePatchOptions = {
  /** When true, empty displayName fails validation (edit profile save). */
  requireDisplayName?: boolean;
};

export function validateProfilePatch(
  input: ProfilePatchInput,
  options: ValidateProfilePatchOptions = {},
): ProfileValidationErrors {
  const errors: ProfileValidationErrors = {};
  const requireDisplayName = options.requireDisplayName ?? false;

  const displayNameError = validateDisplayName(input.displayName, requireDisplayName);
  if (displayNameError) errors.displayName = displayNameError;

  const bioError = validateMaxLength(input.bio, PROFILE_FIELD_LIMITS.bio.max, 'Bio');
  if (bioError) errors.bio = bioError;

  const headlineError = validateMaxLength(input.headline, PROFILE_FIELD_LIMITS.headline.max, 'Headline');
  if (headlineError) errors.headline = headlineError;

  const locationError = validateMaxLength(input.location, PROFILE_FIELD_LIMITS.location.max, 'Location');
  if (locationError) errors.location = locationError;

  const topicsError = validateTopics(input.topics);
  if (topicsError) errors.topics = topicsError;

  const promptsError = validatePrompts(input.profilePrompts);
  if (promptsError) errors.profilePrompts = promptsError;

  return errors;
}

export function hasProfileValidationErrors(errors: ProfileValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}

export function getFirstProfileValidationError(errors: ProfileValidationErrors): string | undefined {
  for (const field of PROFILE_VALIDATION_ORDER) {
    const message = errors[field];
    if (message) return message;
  }
  return undefined;
}

export function formatProfileCharCount(value: string, max: number): string {
  return `${value.length}/${max}`;
}
